import { observer } from "mobx-react";
import { ReactNode, useEffect, useState } from "react";
import InvitationStore, {
  BatchInvitationStore,
  invitationTypeLabel,
} from "../../../stores/InvitationStore";
import { Button, LinkButton } from "../../../components-v2/Button";
import { Alert } from "../../../components-v2/Alert";
import Logo from "../../../assets/img/brand/logoPrimary.svg";
import dCompanyLogo from "../../../assets/img/brand/logoPrimary.svg";
import styles from "../../../Login/LoginComponents.module.css";
import {
  dcompanyFrontpageUrl,
  navigateToRoot,
  openAboutDCompanyUrl,
} from "../../../utils/navigation";
import { LoadingIndicator } from "../../../components-v2/LoadingIndicator";
import { useLoginModel } from "../../../Login/loginDomain";
import { Login } from "../../../Login/loginComponents";
import { Box, Container, VBox } from "../../../components-v2/Box";
import { useLanguage, useRootStore, useTranslation } from "../../../stores/RootStoreContext";
import { Icon } from "../../../components-v2/Icon";
import { FullScreenLayout } from "../../../components-v2/FullScreenLayout";
import { isMobileView } from "../../../utils/screen";
import { Markdown } from "../../../text/Markdown";
import { LanguageSelector } from "../../../components-v2/LanguageSelector";

const InvalidInvitation = observer(function UserInvitationView() {
  const { t } = useTranslation();
  const history = useRootStore().history;
  return (
    <Container gap={"lg"} alignItems={"center"} paddingX={false}>
      <Box display={"flex"} justifyContent={"center"}>
        <img src={Logo} alt="dCompany logo" className={styles.logo} />
      </Box>
      <p>{t("invitation.invalid-invitation")}</p>
      <p>
        <LinkButton label={t("invitation.gotoPortfolio")} onClick={() => navigateToRoot(history)} />
      </p>
    </Container>
  );
});

const AcceptInvitation = observer(function AcceptInvitation({
  store,
  invitationLabel,
}: {
  store: InvitationStore;
  invitationLabel?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Container gap={"lg"} alignItems={"center"} paddingX={false}>
      {invitationLabel}
      <p>
        <Button
          variant={"primary"}
          label={t("invitation.accept-invitation")}
          loading={store.isAcceptLoading}
          disabled={store.isAcceptError}
          onClick={store.acceptInvitation}
        />
      </p>
      {store.isAcceptError && <Alert type="error" message={t("invitation.accept-error")} />}
    </Container>
  );
});

const UserInvitationView = observer(function UserInvitationView({
  store,
}: {
  store: InvitationStore;
}) {
  const { t } = useTranslation();
  const invitationLabel = store.invitation && (
    <VBox textAlign={"center"} gap={"lg"}>
      <h4>{t("invitation.join-dcompany", { invitedByName: store.invitation.invitedByName })}</h4>
      <Box>{t(invitationTypeLabel(store.invitation))}</Box>
      {!store.isLoggedIn && (
        <VBox textAlign={"center"} gap={false}>
          <strong>{t("invitation.accept-by-logging-in-with-bankid")}</strong>
          {t("invitation.accept-by-logging-in-with-bankid.description")}
        </VBox>
      )}
    </VBox>
  );
  const loginModel = useLoginModel({
    disableLogo: true,
    disableIntroText: true,
    canCreateUser: store.invitation?.hasExistingUser !== true,
    username: store.invitation?.recipientEmail,
  });
  return (
    <>
      {store.isLoggedIn ? (
        <AcceptInvitation store={store} invitationLabel={invitationLabel} />
      ) : (
        <Login model={loginModel} customHeading={invitationLabel} />
      )}
    </>
  );
});

const CompanyInvitationView = observer(function CompanyInvitationView({
  store,
}: {
  store: InvitationStore;
}) {
  const { t } = useTranslation();
  const invitationLabel = store.invitation && (
    <p>
      <strong>{store.invitation.invitedByName}</strong> ({store.invitation.invitedByEmail}){" "}
      {t("invitation.has-invited-you-and-your-company")}{" "}
      <strong>{store.invitation.companyName}</strong> {t("invitation.to-join")}{" "}
      <strong>dCompany</strong>.
    </p>
  );
  const loginModel = useLoginModel({
    disableLogo: true,
    disableIntroText: true,
    disableUsernamePasswordLogin: true,
  });

  return (
    <>
      {store.isLoggedIn ? (
        <AcceptInvitation store={store} invitationLabel={invitationLabel} />
      ) : (
        <Login model={loginModel} customHeading={invitationLabel} />
      )}
    </>
  );
});

export const InvitationHeader = observer(({ companyName }: { companyName?: string }) => {
  const logo = dCompanyLogo;
  const lang = useLanguage();
  return isMobileView() ? (
    <Box justifyContent={"space-between"} borderBottom bg paddingY>
      <div>
        <a href={dcompanyFrontpageUrl(lang)} target="_blank">
          <img src={logo} alt="dCompany logo" style={{ maxWidth: 200 }} />
        </a>
      </div>
      <Box alignItems={"center"} marginRight={"lg"} gap={"lg"}>
        <LanguageSelector />
      </Box>
    </Box>
  ) : (
    <Box justifyContent={"space-between"} borderBottom bg padding>
      <Box alignItems={"center"}>
        <div>
          <a href={dcompanyFrontpageUrl(lang)} target="_blank">
            <img src={logo} alt="dCompany logo" style={{ maxWidth: 200 }} />
          </a>
        </div>
        {companyName && <div>{companyName}</div>}
      </Box>
      <Box alignItems={"center"} marginRight={"lg"} gap={"lg"}>
        <LinkButton label={{ key: "about-dcompany" }} onClick={() => openAboutDCompanyUrl(lang)} />
        <LanguageSelector />
      </Box>
    </Box>
  );
});

const InvitationPage = observer(function InvitationPage({
  invitationId,
  invitationToken,
}: {
  invitationId: string;
  invitationToken: string;
}) {
  const { apiService, userStore } = useRootStore();
  const [invitationStore] = useState(
    () => new InvitationStore(userStore, invitationId, invitationToken, apiService),
  );

  useEffect(() => {
    invitationStore.fetchInvitation();
  }, [invitationStore]);

  if (invitationStore.isLoading) {
    return <LoadingIndicator fullscreen />;
  }

  return (
    <FullScreenLayout
      appBar={<InvitationHeader companyName={invitationStore.invitation?.companyName} />}
      centerPanel={
        <Container marginTop={isMobileView() ? "sm" : "md"} paddingTop={"lg"}>
          {invitationStore.isLoadError ? (
            <InvalidInvitation />
          ) : invitationStore.isCompanyInvitation ? (
            <CompanyInvitationView store={invitationStore} />
          ) : (
            <UserInvitationView store={invitationStore} />
          )}
        </Container>
      }
    />
  );
});

export const BatchInvitationPage = observer(function BatchInvitationPage({
  batchInvitationId,
  invitationToken,
}: {
  batchInvitationId: string;
  invitationToken: string;
}) {
  const { apiService, userStore } = useRootStore();
  const [store] = useState(
    () => new BatchInvitationStore(userStore, batchInvitationId, invitationToken, apiService),
  );

  useEffect(() => {
    store.fetchBatchInvitation();
  }, [store]);

  if (store.isLoading) {
    return <LoadingIndicator fullscreen />;
  }

  return (
    <FullScreenLayout
      appBar={<InvitationHeader />}
      centerPanel={
        <Container marginTop={isMobileView() ? "sm" : "md"} paddingTop={"lg"}>
          {store.isLoadError ? <InvalidInvitation /> : <BatchUserInvitationView store={store} />}
        </Container>
      }
    />
  );
});

const BatchUserInvitationView = observer(function BatchUserInvitationView({
  store,
}: {
  store: BatchInvitationStore;
}) {
  const { t } = useTranslation();
  const invitationLabel = store.invitation && (
    <VBox>
      <Markdown
        value={t("invitation.has-invited-you-to-their-company-batch", {
          invitedByName: store.invitation.invitedByName,
        })}
      />
      <VBox alignItems={"start"} padding>
        {store.invitation.companyNames.map((companyName) => (
          <Box key={companyName} alignItems={"center"}>
            <Icon name={"check-circle"} size="sm" /> {companyName}
          </Box>
        ))}
      </VBox>
      {!store.isLoggedIn && t("invitation.login-to-confirm")}
    </VBox>
  );
  const loginModel = useLoginModel({
    disableIntroText: true,
    disableLogo: true,
    canCreateUser: store.invitation?.hasExistingUser !== true,
    username: store.invitation?.recipientEmail,
  });

  return (
    <Container marginTop={isMobileView() ? "sm" : "md"} paddingTop={"lg"}>
      {store.isLoggedIn ? (
        <BatchAcceptInvitation store={store} invitationLabel={invitationLabel} />
      ) : (
        <Login model={loginModel} customHeading={invitationLabel} />
      )}
    </Container>
  );
});

const BatchAcceptInvitation = observer(function BatchAcceptInvitation({
  store,
  invitationLabel,
}: {
  store: BatchInvitationStore;
  invitationLabel?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Container gap={"lg"} alignItems={"center"} paddingX={false}>
      {invitationLabel}
      <p>
        <Button
          variant={"primary"}
          label={t("invitation.accept-invitation")}
          loading={store.isAcceptLoading}
          disabled={store.isAcceptError}
          onClick={store.acceptInvitation}
        />
      </p>
      {store.isAcceptError && <Alert type="error" message={t("invitation.accept-error")} />}
    </Container>
  );
});

export default InvitationPage;
